import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useApi } from '../api-context';
import { queryKeys } from '../query-keys';
import {
  adminTransactionListSchema,
  type AdminTransactionList,
  type TransactionStatut,
} from '../schemas';
import { useToken } from './use-token';

export type AdminTransactionListParams = {
  page?: number;
  limit?: number;
  search?: string;
  statut?: TransactionStatut | '';
  /** Profondeur d'historique, en mois. Ignorée si `cabinetId` est fourni. */
  mois?: number;
  /** Restreint à un cabinet : Stripe filtre alors par client. */
  cabinetId?: string;
};

/**
 * Historique de facturation de toute la plateforme. Réservé au back-office :
 * contrairement à `usePayments`, le backend ne scope à aucun cabinet.
 */
export function useAdminTransactions({
  page = 1,
  limit = 20,
  search = '',
  statut = '',
  mois = 12,
  cabinetId = '',
}: AdminTransactionListParams = {}) {
  const api = useApi();
  const token = useToken();

  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    mois: String(mois),
  });
  if (search) query.set('search', search);
  if (statut) query.set('statut', statut);
  if (cabinetId) query.set('cabinetId', cabinetId);

  return useQuery({
    queryKey: queryKeys.adminBilling.transactions({
      page,
      limit,
      search,
      statut,
      mois,
      cabinetId,
    }),
    queryFn: () =>
      api.get<AdminTransactionList>(`/admin/billing/transactions?${query}`, {
        schema: adminTransactionListSchema,
      }),
    enabled: token !== null,
    // Les factures viennent de Stripe : la réponse est lente et ne bouge pas
    // d'une minute à l'autre, autant garder la page précédente à l'écran.
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}
